import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from "@angular/core";
import { ObserveArray } from "@calaosoft/osapp-common/observable/decorators/observe-array.decorator";
import { ObservableArray } from "@calaosoft/osapp-common/observable/models/observable-array";
import { secure } from "@calaosoft/osapp-common/rxjs/operators/secure";
import { ArrayHelper } from "@calaosoft/osapp-common/utils/helpers/arrayHelper";
import { Observable, map, takeUntil } from "rxjs";
import { DestroyableComponentBase } from "../../../../utils/components/destroyable-component-base";
import { ICategoryClick } from "./models/ICategoryClick";
import { ICategoryItemsByLine } from "./models/icategory-items-by-line";
import { ICountingCategoryItem } from "./models/icounting-category-item";

@Component({
	selector: "calao-categories",
	templateUrl: "./categories.component.html",
	styleUrls: ["./categories.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class CategoriesComponent extends DestroyableComponentBase {
	//#region FIELDS

	private static readonly C_LOG_ID = "CTGRS.C::";

	@Output("categoryClick") private readonly moCategoryClickEvent = new EventEmitter<ICategoryClick>();
	@Output("categoryItemClicked") private readonly moCategoryItemClickedEvent =
		new EventEmitter<ICountingCategoryItem>();

	//#endregion FIELDS

	//#region FIELDS

	@Input({ required: true }) public readonly taskId?: string;

	/** Ensemble des catégories avec une quantité. */
	@Input() public categories?: ICountingCategoryItem[];
	@ObserveArray<CategoriesComponent>("categories")
	public readonly obsCategories = new ObservableArray<ICountingCategoryItem>();

	/** Tableau des lignes des tuiles à afficher, contenant chacune les colonnes de la ligne associée. */
	@Input() public categoryItemsByLines?: ICategoryItemsByLine[];
	@ObserveArray<CategoriesComponent>("categoryItemsByLines")
	public readonly obsCategoryItemsByLines = new ObservableArray<ICategoryItemsByLine>();

	public readonly hasItemsByLine$: Observable<boolean>;

	//#endregion

	//#region METHODS

	public constructor() {
		super();
		this.hasItemsByLine$ = this.getHasItemsByLine$().pipe(secure(this));

		this.logOversizedColumns$().pipe(takeUntil(this.destroyed$)).subscribe();
	}

	private getHasItemsByLine$(): Observable<boolean> {
		return this.obsCategoryItemsByLines.changes$.pipe(
			map((paItems: ICategoryItemsByLine[]) => ArrayHelper.hasElements(paItems))
		);
	}

	private logOversizedColumns$(): Observable<void> {
		return this.obsCategoryItemsByLines.changes$.pipe(
			map((paItemLines: ICategoryItemsByLine[]) => {
				// Si une ligne a plus de trois éléments à afficher, on logue une erreur.
				if (paItemLines.some((poItemLine: ICategoryItemsByLine) => poItemLine.items.length > 3))
					console.error(
						`${CategoriesComponent.C_LOG_ID}Task '${this.taskId}' has too many categories on the same line, only the first three will be displayed !`
					);
			})
		);
	}

	public raiseCategoryItemClicked(poValue: ICountingCategoryItem): void {
		this.moCategoryItemClickedEvent.emit(poValue);
	}

	public onCategoryClick(poValue: ICategoryClick): void {
		this.moCategoryClickEvent.emit(poValue);
	}

	//#endregion
}
